import { ArrowLeft, Check, Gauge, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    useGetAllSubscriptionPlansQuery,
    useRenewSubscriptionMutation,
    useUpgradeSubscriptionMutation,
} from "../../../redux/services/subscription";
import { usePermission } from "../../../utils/permissions";
import { splitPlanFeatures } from "../../../utils/planFeatures";

// Plan descriptions are UI copy — not stored in the backend
const planDescriptions: Record<string, string> = {
    starter:  "Perfect for solo operators and small businesses just getting started with logistics.",
    growth:   "Built for growing operations that need multi-branch visibility and active tracking.",
    business: "Enterprise-ready with unlimited capacity, advance analytics and priority support.",
};

const isValidId = (id?: string | null): id is string =>
    typeof id === "string" &&
    id.trim() !== "" &&
    id !== "undefined" &&
    id !== "null";

interface ComparePlansProps {
    setShowComparison: (value: boolean) => void;
    activePlan: string | undefined;
    cycle: string | undefined;
    subStatus: string | undefined;
    companyId: string;
    subscriptionId?: string;
    // Plan to highlight, e.g. the upgrade suggested by a plan-limit error.
    suggestedPlanId?: string;
}

export default function ComparePlans({
    setShowComparison,
    activePlan,
    cycle,
    subStatus,
    companyId,
    subscriptionId,
    suggestedPlanId,
}: ComparePlansProps) {
    const [isCycle, setIsCycle] = useState<"Monthly" | "Yearly">(
        cycle === "Yearly" ? "Yearly" : "Monthly"
    );
    const [hoverPlan, setHoverPlan] = useState<"starter" | "growth" | "business" | null>(null);

    const [isRedirecting, setIsRedirecting] = useState(false);
    const [loadingPlanIds, setLoadingPlanIds] = useState<Record<string, boolean>>({});
    const [isShowingToast, setIsShowingToast] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const showErrorToast = (message: string) => {
        if (isShowingToast) return;
        setIsShowingToast(true);
        toast.error(message);
        setTimeout(() => setIsShowingToast(false), 3000); // Reset after 3 seconds 
    };

    const { data: subscriptionPlans, isLoading: plansLoading } = useGetAllSubscriptionPlansQuery();

    // Show the suggested plan's billing cycle and scroll it into view once.
    const suggestedCardRef = useRef<HTMLDivElement | null>(null);
    const [suggestedShown, setSuggestedShown] = useState(false);
    useEffect(() => {
        if (!suggestedPlanId || !subscriptionPlans?.data) return;
        const inYearly = subscriptionPlans.data.yearly?.some((p: any) => p?.id === suggestedPlanId);
        const inMonthly = subscriptionPlans.data.monthly?.some((p: any) => p?.id === suggestedPlanId);
        if (inYearly && !inMonthly) setIsCycle("Yearly");
        else if (inMonthly) setIsCycle("Monthly");
    }, [suggestedPlanId, subscriptionPlans]);
    useEffect(() => {
        if (suggestedShown || !suggestedCardRef.current) return;
        suggestedCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        setSuggestedShown(true);
    }, [suggestedShown, isCycle, subscriptionPlans]);
    const [renewSubscription] = useRenewSubscriptionMutation();
    const [upgradeSubscription] = useUpgradeSubscriptionMutation();

    const { can } = usePermission();
    const canRenew = can("renew subscription");
    const canUpgrade = can("upgrade subscription");

    const resolvedSubscriptionId = isValidId(subscriptionId) ? subscriptionId : undefined;

    // Plans from the API for the currently selected billing cycle (cards)
    const displayPlans: any[] = (
        isCycle === "Yearly"
            ? subscriptionPlans?.data?.yearly
            : subscriptionPlans?.data?.monthly
    )?.filter((p: any) => p?.name?.toLowerCase().includes("individual")) || [];

    // Monthly plans used for the feature comparison table
    const individualPlans: any[] =
        subscriptionPlans?.data?.monthly?.filter((plan: any) =>
            plan?.name?.toLowerCase().includes("individual")
        ) || [];

    const formatPrice = (price: number | string) => {
        const num = typeof price === "string" ? parseFloat(price) : price;
        if (isNaN(num)) return String(price);
        return `₦${num.toLocaleString("en-NG")}`;
    };

    const activePlanClr =
    subStatus === "active"
      ? "bg-green-500 text-white"
      : subStatus === "canceled"
      ? "bg-yellow-500 text-white"
      : "bg-red-500 text-white"

    // Feature keys only; each plan's limit lines ("Up to 2 users") get their own row.
    const allFeatures: string[] = Array.from(
        new Set(
            (individualPlans || []).flatMap((p: any) => splitPlanFeatures(p?.features).features)
        )
    );
    const hasPlanLimits = (individualPlans || []).some(
        (p: any) => splitPlanFeatures(p?.features).limits.length > 0
    );

    const redirectToCheckout = (checkoutUrl: string | null | undefined) => {
        if (checkoutUrl) {
            setIsRedirecting(true);
            setTimeout(() => {
                window.location.href = checkoutUrl;
            }, 300);
        } else {
            console.error("❌ No checkout_url returned from API");
            alert("Payment URL not received. Please try again.");
        }
    };


    const handleUpgrade = async (planId: string) => {
        if (!isValidId(companyId)) {
            console.error("❌ Cannot upgrade: companyId missing.", { companyId });
            return;
        }
        if (!isValidId(planId)) {
            console.error("❌ Cannot upgrade: planId missing.", { planId });
            return;
        }
        if (isProcessing) return; // Prevent multiple calls

        setLoadingPlanIds(prev => ({ ...prev, [planId]: true }));
        setIsProcessing(true);
        try {
            const res = await upgradeSubscription({
                companyId,
                plan_id: planId,
                billing_cycle: isCycle.toLowerCase(),
            }).unwrap();
            redirectToCheckout(res?.checkout_url ?? res?.data?.checkout_url);
        } catch (err: any) {
            console.error("❌ Upgrade Error:", err);

            // Extract error message from the response
            const errorMessage = err?.data?.message ||
                               err?.message ||
                               "Failed to process subscription upgrade. Please try again.";

            showErrorToast(errorMessage);
        } finally {
            setLoadingPlanIds(prev => ({ ...prev, [planId]: false }));
            setIsProcessing(false);
        }
    };

    const handleRenew = async (planId: string) => {
        if (!isValidId(resolvedSubscriptionId)) {
            console.error("❌ Cannot renew: subscriptionId missing.", {
                resolvedSubscriptionId,
                subscriptionId,
            });
            return;
        }
        if (!isValidId(planId)) {
            console.error("❌ Cannot renew: planId missing.", { planId });
            return;
        }
        if (isProcessing) return; // Prevent multiple calls

        setLoadingPlanIds(prev => ({ ...prev, [planId]: true }));
        setIsProcessing(true);
        try {
            const res = await renewSubscription({
                companySubscriptionId: resolvedSubscriptionId,
                plan_id: planId,
                billing_cycle: isCycle.toLowerCase(),
            }).unwrap();
            redirectToCheckout(res?.checkout_url ?? res?.data?.checkout_url);
        } catch (err: any) {
            console.error("❌ Renew Error:", err);

            // Extract error message from the response
            const errorMessage = err?.data?.message ||
                               err?.message ||
                               "Failed to process subscription renewal. Please try again.";

            showErrorToast(errorMessage);
        } finally {
            setLoadingPlanIds(prev => ({ ...prev, [planId]: false }));
            setIsProcessing(false);
        }
    };

    const handlePlanAction = async (planTitle: string, planId: string) => {
        const isSamePlan = activePlan === planTitle && cycle === isCycle;
        if (isSamePlan && subStatus !== "active") {
            if (!canRenew) {
                showErrorToast("You don't have permission to renew the subscription");
                return;
            }
            return handleRenew(planId);
        }
        if (!canUpgrade) {
            showErrorToast("You don't have permission to upgrade the subscription");
            return;
        }
        return handleUpgrade(planId);
    };

    const getButtonLabel = (planTitle: string) => {
        const isSamePlan = activePlan === planTitle && cycle === isCycle;
        if (isSamePlan && subStatus === "active") return "Current Plan";
        if (isSamePlan && subStatus !== "active") return "Renew Plan";
        return "Upgrade Plan";
    };

    const isRenewNeeded = (planTitle: string) =>
        activePlan === planTitle && cycle === isCycle && subStatus !== "active";

    const isButtonDisabled = (planTitle: string, planId: string) => {
        const isCurrentActivePlan =
            activePlan === planTitle && cycle === isCycle && subStatus === "active";
        const renewNeeded = isRenewNeeded(planTitle);
        return (
            isCurrentActivePlan ||
            Boolean(loadingPlanIds[planId]) ||
            isRedirecting ||
            isProcessing ||
            !isValidId(companyId) ||
            (renewNeeded && !isValidId(resolvedSubscriptionId)) ||
            (renewNeeded ? !canRenew : !canUpgrade)
        );
    };

    const getButtonTitle = (planTitle: string, planId: string) => {
        const isCurrentActivePlan =
            activePlan === planTitle && cycle === isCycle && subStatus === "active";
        if (isCurrentActivePlan) return undefined;
        if (isRenewNeeded(planTitle) && !canRenew) {
            return "You don't have permission to renew the subscription";
        }
        if (!isRenewNeeded(planTitle) && !canUpgrade) {
            return "You don't have permission to upgrade the subscription";
        }
        return undefined;
    };


    return (
        <div className="relative p-4 sm:p-6 bg-gray-50 min-h-screen space-y-8">

            {/* Full-screen overlay while redirecting to Monnify */}
            {isRedirecting && (
                <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
                    <Loader2 className="w-10 h-10 animate-spin text-[#1E3A8A] mb-4" />
                    <p className="text-lg font-semibold text-[#1E3A8A]">
                        Redirecting to payment...
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                        Please do not close this page.
                    </p>
                </div>
            )}

            {/*  Show skeleton/loader while plans are loading for the first time */}
            {plansLoading && (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
                    <span className="ml-3 text-gray-500">Loading plans...</span>
                </div>
            )}

            <div className="flex items-center gap-3 sm:gap-5">
                <button
                    onClick={() => setShowComparison(false)}
                    className="flex-shrink-0 flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
                >
                    <ArrowLeft size={16} />
                </button>
                <h1 className="text-base sm:text-xl font-semibold text-[#143887]">
                    Compare Our Plans: What's Right for You?
                </h1>
            </div>

            <div className="flex items-center flex-wrap gap-2">
                <button
                    onClick={() => setIsCycle("Monthly")}
                    className={
                        isCycle === "Monthly"
                            ? "bg-[#1E3A8A] text-white px-4 py-2 rounded-lg shadow hover:bg-[#1E40AF]"
                            : "bg-white text-[#1E3A8A] border border-[#1E3A8A] px-4 py-2 rounded-lg shadow hover:bg-[#1E40AF] hover:text-white"
                    }
                >
                    Monthly Billed
                </button>
                <hr className="h-5 w-[1px] bg-gray-300" />
                <button
                    onClick={() => setIsCycle("Yearly")}
                    className={
                        isCycle === "Yearly"
                            ? "bg-[#1E3A8A] text-white px-4 py-2 rounded-lg shadow hover:bg-[#1E40AF]"
                            : "bg-white text-[#1E3A8A] border border-[#1E3A8A] px-4 py-2 rounded-lg shadow hover:bg-[#1E40AF] hover:text-white"
                    }
                >
                    Yearly Billed
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {displayPlans.map((plan: any) => {
                    const planKey = (plan.name?.toLowerCase().split(" ")[1] || "") as
                        | "starter"
                        | "growth"
                        | "business";
                    const isActivePlan =
                        hoverPlan === null && activePlan === plan.name && cycle === isCycle;
                    const isHoveredPlan = hoverPlan === planKey;
                    const selectedPlanId = plan.id;
                    const isSuggestedPlan = plan.id === suggestedPlanId;
                    const { limits: planLimits, features: planFeatures } = splitPlanFeatures(plan.features);

                    return (
                        <div
                            key={plan.id}
                            ref={isSuggestedPlan ? suggestedCardRef : undefined}
                            onMouseEnter={() => setHoverPlan(planKey)}
                            onMouseLeave={() => setHoverPlan(null)}
                            className={`relative flex flex-col justify-between border rounded-xl p-4 sm:p-6 bg-white shadow-sm cursor-pointer ${
                                isActivePlan || isHoveredPlan || isSuggestedPlan ? "ring-2 ring-[#1A4AB2]" : ""
                            }`}
                        >
                            <div>
                                <h2 className="text-[16px] font-bold text-[#050d20fa]">{plan.name}</h2>
                                <p className="text-sm text-gray-700 mt-1">
                                    {planDescriptions[planKey] || ""}
                                </p>
                                <p className="text-3xl font-bold text-[#1E3A8A] mt-4">
                                    {formatPrice(plan.price)}
                                    <span className="text-gray-500 text-sm">
                                        {isCycle === "Yearly" ? "/yr" : "/mo"}
                                    </span>
                                </p>
                                {planLimits.length > 0 && (
                                    <ul className="list-none mt-4 space-y-1.5 pb-3 border-b border-gray-100">
                                        {planLimits.map((limit: string) => (
                                            <li key={limit} className="flex items-center gap-2 text-sm font-semibold text-[#1E3A8A]">
                                                <Gauge size={16} className="shrink-0" aria-hidden="true" />
                                                {limit}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <ul className="list-none list-inside mt-4 text-sm text-gray-700">
                                    {planFeatures.map((feature: string) => (
                                        <li key={feature} className="flex items-center gap-2 my-1">
                                            <span className="bg-green-200 rounded">
                                                <Check size={16} />
                                            </span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="flex justify-center items-center mt-6">
                                <button
                                    onClick={() => handlePlanAction(plan.name, selectedPlanId)}
                                    disabled={isButtonDisabled(plan.name, selectedPlanId)}
                                    title={getButtonTitle(plan.name, selectedPlanId)}
                                    className="w-full border border-[#1E3A8A] text-[#1E3A8A] px-4 py-2 rounded-lg hover:bg-[#1970E6] hover:text-white transition-colors duration-300 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500"
                                >
                                    {loadingPlanIds[selectedPlanId] ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Loader2 size={16} className="animate-spin" />
                                            Processing...
                                        </span>
                                    ) : (
                                        getButtonLabel(plan.name)
                                    )}
                                </button>
                            </div>
                            {isSuggestedPlan && !(activePlan === plan.name && cycle === isCycle) && (
                                <div className="absolute -top-[14px] left-1/2 transform -translate-x-1/2 bg-[#1A4AB2] text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                                    Suggested for you
                                </div>
                            )}
                            {activePlan === plan.name && cycle === isCycle && (
                                <div className={`absolute -top-[14px] left-1/2 transform -translate-x-1/2 ${activePlanClr} text-xs font-semibold px-3 py-1 rounded-full`}>
                                    {subStatus === "active" ? "Active Plan" : subStatus === "canceled" ? "Canceled Plan" : "Expired Plan"}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="rounded-xl border shadow-sm overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm border-collapse">
                    <thead>
                        <tr className="bg-[#F3F4F6] border-b text-gray-700">
                            <th className="p-5 text-left w-1/3 whitespace-nowrap">FEATURES</th>
                            {individualPlans?.map((plan: any) => (
                                <th key={plan.name} className="p-5 text-center whitespace-nowrap">
                                    {plan.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white text-gray-700">
                        {hasPlanLimits && (
                            <tr className="border-b bg-[#F8FAFF]">
                                <td className="p-4 font-semibold text-[#1E3A8A]">Plan limits</td>
                                {individualPlans?.map((plan: any) => (
                                    <td key={plan.name} className="p-4 text-center align-top">
                                        <ul className="space-y-1 text-xs font-medium text-gray-800">
                                            {splitPlanFeatures(plan.features).limits.map((limit: string) => (
                                                <li key={limit}>{limit}</li>
                                            ))}
                                        </ul>
                                    </td>
                                ))}
                            </tr>
                        )}
                        {allFeatures.map((feature: string, i: number) => (
                            <tr key={i} className="border-b last:border-b-0">
                                <td className="p-4 font-medium">{feature}</td>
                                {individualPlans?.map((plan: any) => {
                                    const formattedFeatures = splitPlanFeatures(plan.features).features;
                                    const includes = formattedFeatures.includes(feature);
                                    return (
                                        <td key={plan.name} className="p-4 text-center">
                                            {includes ? (
                                                <span className="bg-green-200 rounded inline-flex items-center justify-center">
                                                    <Check size={16} />
                                                </span>
                                            ) : (
                                                <span className="bg-red-400 rounded inline-flex items-center justify-center">
                                                    <X size={16} />
                                                </span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}